/**
 * Extract all Optum referral IDs from a block of text.
 * Matches both the legacy and current title formats:
 * Legacy ("referral ID" phrase):
 * - "referral ID 1112935"
 * - "referral ID1112935"
 * - "referral ID: 1112935"
 * - "OptimalCare - referral ID  1112935"
 * Current (no phrase):
 * - "Optimal Care - 1112935"
 * - "OptimalCare - 1112935"
 * Returns the LAST occurrence found.
 *
 * Accepts a string, an array of strings (e.g. the SDK's `reasons` shape),
 * or null/undefined. Arrays are joined with newlines before scanning so a
 * single OptimalCare ID line in any entry is matched.
 */
export function getOptumReferralIdFromNotes(input?: string | string[] | null): string | undefined {
  const text = Array.isArray(input) ? input.join('\n') : input;
  if (typeof text !== 'string' || text.length === 0) return undefined;

  // Supports spaces, real newlines, and literal "\n"/"\r" sequences between tokens, because
  // notes read back from an EHR arrive reflowed.
  const ws = '(?:\\s|\\\\n|\\\\r)*';

  // The id counts only when it OPENS a line (or a pipe-delimited segment, which is how the retired
  // Leading Reach template packed a whole note onto one line). Anchoring this way is what keeps
  // body fields out: notes now carry "Leading reach referralID: <numeric>", and "Market"/"Tier" can
  // hold an Optum plan name containing the "Optimal Care" brand plus digits. A mid-line scan lets
  // any of those beat the title under the last-occurrence rule, and the app then resolves
  // prior-auth status and resubmit-dedupe against the wrong referral.
  // Excluding known labels instead was tried and rejected: a denylist only covers the fields that
  // exist when it is written, and cannot cover free text like "Reason: see prior referral ID 9".
  const lineStart = `(?:^|[\\n|]|\\\\n)${ws}`;
  const regex = new RegExp(
    `${lineStart}(?:\\b(Optimal${ws}Care)${ws}[:\\-–—]${ws})?` +
      `(?:\\breferral${ws}ID(?=\\b|\\d)${ws}[:\\-–—]?${ws})?(\\d+)`,
    'gim',
  );

  // Line position alone is forgeable: a pipe or a <br />-turned-newline inside a clinician's Reason
  // manufactures a line opening with "referral ID 9999", which would then win the last-occurrence
  // rule. A genuine note always carries the brand in its title, so a brand-anchored match outranks
  // a bare "referral ID" one. The bare form is only consulted when no branded title exists at all,
  // which is the shape the legacy callers pass (e.g. plain "referral ID: 12345").
  const branded: string[] = [];
  const bare: string[] = [];

  for (const match of text.matchAll(regex)) {
    // One of the two title anchors must have participated — otherwise this is just a line that
    // happens to start with a number (an NPI or phone line), not a referral title.
    if (!/optimal|referral/i.test(match[0])) continue;
    const [, brand, id] = match;
    if (!id) continue;
    (brand ? branded : bare).push(id);
  }

  const ids = branded.length > 0 ? branded : bare;

  return ids.length > 0 ? ids[ids.length - 1] : undefined;
}
